// frontend/src/components/upload/UploadZone.jsx
import React, { useCallback, useState } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';

const UploadZone = ({ onFileSelect }) => {
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState(null);

  const validateFile = useCallback((file) => {
    const errors = [];
    
    // Vérification du type de fichier
    const allowedTypes = ['.csv', 'text/csv', 'application/csv'];
    const fileExtension = file.name.toLowerCase().split('.').pop();
    
    if (fileExtension !== 'csv' && !allowedTypes.includes(file.type)) {
      errors.push('Seuls les fichiers CSV sont acceptés');
    }

    // Vérification de la taille (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      errors.push('Le fichier ne peut pas dépasser 50MB');
    }

    // Vérification de la taille minimale
    if (file.size < 100) {
      errors.push('Le fichier semble trop petit pour contenir des données valides');
    }

    return errors;
  }, []);

  const handleFiles = useCallback((files) => {
    const file = files[0];
    if (!file) return;

    const errors = validateFile(file);
    
    if (errors.length > 0) {
      setFileError(errors[0]);
      return;
    }

    setFileError(null);
    onFileSelect(file);
  }, [validateFile, onFileSelect]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInput = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  return (
    <div className="space-y-6">
      
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          Format requis pour votre fichier CSV
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Colonnes obligatoires:</h4>
            <ul className="space-y-1 text-blue-700">
              <li>• <code className="bg-blue-100 px-1 rounded">timestamp</code> - Date et heure (ISO format)</li>
              <li>• <code className="bg-blue-100 px-1 rounded">puissance_active_kw</code> - Puissance en kW</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Exemple de format:</h4>
            <div className="bg-blue-100 p-2 rounded text-xs font-mono">
              timestamp,puissance_active_kw<br/>
              2024-01-01T00:00:00,245.5<br/>
              2024-01-01T00:30:00,231.2
            </div>
          </div>
        </div>
      </div>

      {/* Zone de drop */}
      <div
        className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-200 ${
          dragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".csv"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label="Sélectionner un fichier CSV"
        />

        <div className="space-y-4">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
            dragActive ? 'bg-blue-100' : 'bg-gray-100'
          }`}>
            <Upload className={`w-8 h-8 transition-colors ${
              dragActive ? 'text-blue-600' : 'text-gray-500'
            }`} />
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {dragActive ? 'Relâchez pour télécharger' : 'Glissez-déposez votre fichier CSV'}
            </h3>
            <p className="text-gray-600 mb-4">
              ou cliquez pour sélectionner un fichier
            </p>
            
            <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                CSV uniquement
              </span>
              <span>Max 50MB</span>
              <span>Pas 30min recommandé</span>
            </div>
          </div>
        </div>
      </div>

      {/* Erreur de validation */}
      {fileError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Fichier non valide</span>
          </div>
          <p className="text-red-600 mt-1">{fileError}</p>
        </div>
      )}

      {/* Exemples de fichiers */}
      <div className="border-t pt-6">
        <h4 className="font-medium text-gray-900 mb-3">
          Pas de fichier ? Téléchargez nos exemples :
        </h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <button className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <FileText className="w-5 h-5 text-blue-600" />
            <div className="text-left">
              <div className="font-medium">Exemple industriel</div>
              <div className="text-sm text-gray-600">1 an de données BT &gt; 36kVA</div>
            </div>
          </button>
          
          <button className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <FileText className="w-5 h-5 text-green-600" />
            <div className="text-left">
              <div className="font-medium">Exemple tertiaire</div>
              <div className="text-sm text-gray-600">6 mois de données HTA</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadZone;
